export const fieldConfig = {
  core: {
    title: "Core Parameters",
    fields: [
      {
        name: "name",
        label: "Simulation Name",
        type: "text",
        defaultValue: "",
        required: true,
        helpText: "Enter a descriptive name for this simulation run.",
      },
      {
        name: "container_batch",
        label: "Container Batch",
        type: "api_select",
        required: true,
        helpText: "Select battery container specification.",
      },
      {
        name: "pcs",
        label: "Pcs",
        type: "api_select",
        required: true,
        helpText: "Select power conversion system.",
      },
      {
        name: "simulation_type",
        label: "Simulation Type",
        type: "select",
        defaultValue: "closed_loop",
        required: true,
        options: [
          { value: "open_loop", label: "Without Battery Augmentation" },
          { value: "closed_loop", label: "Manual Battery Augmentation" },
          { value: "auto_closed_loop", label: "Auto Battery Augmentation" },
        ],
        helpText: "Determines which augmentation algorithm is used when running the simulation.",
      },
      {
        name: "operation_hour",
        label: "Storage Duration",
        type: "number",
        defaultValue: 4,
        units: "Hours",
        required: true,
        min: 1,
        max: 24,
        helpText: "Storage discharge duration.",
      },
    ],
  },
  settings: {
    title: "Simulation Settings",
    fields: [
      {
        name: "start_year",
        label: "Start Year",
        type: "number",
        defaultValue: 2026,
        units: "Year",
        required: true,
        min: 2000,
        max: 2100,
        helpText: "Project commencement year.",
      },
      {
        name: "simulation_tenure",
        label: "Simulation Tenure",
        type: "number",
        defaultValue: 20,
        units: "Years",
        required: true,
        min: 1,
        max: 50,
        helpText: "Total simulation lifespan.",
      },
      {
        name: "energy_poi_mwh",
        label: "Contracted Energy at POI (MWh)",
        type: "number",
        defaultValue: 100.0,
        units: "MWh",
        required: true,
        min: 0.1,
        helpText: "Contracted active discharge energy target at POI.",
      },
    ],
  },
  quantities: {
    title: "Quantities",
    fields: [
      {
        name: "container_nos",
        label: "Container Qty Nos",
        type: "number",
        defaultValue: 10,
        units: "Nos",
        required: true,
        min: 1,
        helpText: "Number of initially installed battery containers.",
      },
      {
        name: "pcs_nos",
        label: "PCS Quantity Nos",
        type: "number",
        defaultValue: 10,
        units: "Nos",
        required: true,
        min: 1,
        helpText: "Number of power conversion system units.",
      },
    ],
  },
  losses: {
    title: "System Losses",
    fields: [
      {
        name: "transmission_line_220kv_loss",
        label: "220Kv Transmission line loss(%)",
        type: "number",
        defaultValue: 0.5,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "transformer_220kv_loss",
        label: "220/33 KV transformer loss (%)",
        type: "number",
        defaultValue: 0.15,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "transmission_line_400kv_loss",
        label: "400kV Transmission line loss (%)",
        type: "number",
        defaultValue: 0.5,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "transformer_400kv_loss",
        label: "400/220KV transformer loss (%)",
        type: "number",
        defaultValue: 0.15,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "solar_aux_power",
        label: "Solar Aux power (kW)",
        type: "number",
        defaultValue: 1.0,
        units: "kW",
        required: true,
        min: 0,
        step: 0.01,
      },
      {
        name: "ht_ac_cable_loss",
        label: "HT AC cable loss (%)",
        type: "number",
        defaultValue: 0.1,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "idt_loss",
        label: "IDT Loss (%)",
        type: "number",
        defaultValue: 1.0,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "lt_ac_cable_loss",
        label: "LT AC cable loss (%)",
        type: "number",
        defaultValue: 0.33,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "pcs_loss",
        label: "PCS Loss (%)",
        type: "number",
        defaultValue: 1.5,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "lt_dc_cable_loss",
        label: "LT DC cable loss (%)",
        type: "number",
        defaultValue: 0.12,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
      {
        name: "bess_plant_unavailability",
        label: "BESS plant availability (%)",
        type: "number",
        defaultValue: 1.5,
        units: "%",
        required: true,
        min: 0,
        max: 100,
        step: 0.01,
      },
    ],
  },
};

export const defaultFormValues = {
  name: "",
  start_year: "",
  simulation_tenure: "",
  operation_hour: "",
  simulation_type: "closed_loop",
  container_batch: "",
  pcs: "",
  container_nos: "",
  pcs_nos: "",
  min_aug_period: "",
  min_aug_container: "",
  max_storage_capacity: "",
  energy_poi_mwh: "",
  transmission_line_220kv_loss: "",
  transmission_line_400kv_loss: "",
  transformer_220kv_loss: "",
  transformer_400kv_loss: "",
  solar_aux_power: "",
  use_220kv_loss: true,
  use_400kv_loss: false,
  use_transformer_220_loss: true,
  use_transformer_400_loss: false,
  ht_ac_cable_loss: "",
  idt_loss: "",
  lt_ac_cable_loss: "",
  pcs_loss: "",
  lt_dc_cable_loss: "",
  bess_plant_unavailability: "",
  purchase_years: [],
  tl_volt_stage_1: 220,
  tl_volt_stage_2: 400,
};
